import { useAuth } from 'shared/hooks/useAuth';
import { StatTile } from 'shared/components/ui/StatTile';
import { ChartCard } from 'shared/components/charts/ChartCard';
import { RevenueTrendChart } from 'shared/components/charts/RevenueTrendChart';
import { ProductMixBar } from 'shared/components/charts/ProductMixBar';
import { MagnitudeBarList } from 'shared/components/charts/MagnitudeBarList';
import { ArenasTable } from 'modules/admin/dashboard/components/ArenasTable';
import { TransactionsTable } from 'modules/admin/dashboard/components/TransactionsTable';
import { DisputesQueue } from 'modules/admin/dashboard/components/DisputesQueue';
import { ModerationQueue } from 'modules/admin/dashboard/components/ModerationQueue';
import { WebhookEventsTable } from 'modules/admin/dashboard/components/WebhookEventsTable';
import { CHART_INK } from 'shared/constants/chartPalette';
import { useAdminDashboard } from 'modules/admin/dashboard/useAdminDashboard';
import { AsyncSection } from 'shared/components/ui/LoadingState';
import { Skeleton, SkeletonChartCard, SkeletonStatGrid } from 'shared/components/ui/Skeleton';

const formatCurrency = (value: number) =>
  `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: value % 1 !== 0 ? 2 : 0 })}`;
const formatArenaCount = (value: number) => `${value} ${value === 1 ? 'arena' : 'arenas'}`;
const formatPercent = (value: number) => `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

export function AdminDashboardPage() {
  const { user } = useAuth();
  const {
    platformKpis,
    arenas,
    transactions,
    disputes,
    moderationQueue,
    mrrTrend,
    gmvTrend,
    revenueSplit,
    arenasByPlan,
    arenasByStatus,
    webhookKpis,
    webhookSuccessTrend,
    webhookEvents,
    isLoading,
    error,
  } = useAdminDashboard();

  return (
    <section className="mx-auto max-w-6xl px-4 py-8">
      <h1 className="font-heading text-2xl font-bold text-ink">Administração da Plataforma</h1>
      <p className="mt-1 text-ink-secondary">Bem-vindo, {user?.name}.</p>

      <AsyncSection
        isLoading={isLoading}
        error={error}
        loadingLabel="Carregando painel administrativo..."
        skeleton={
          <>
            <SkeletonStatGrid count={5} className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" />
            <Skeleton className="mt-10 h-5 w-52" />
            <Skeleton className="mt-2 h-3 w-80" />
            <div className="mt-4 grid gap-6 lg:grid-cols-2">
              <SkeletonChartCard kind="line" />
              <SkeletonChartCard kind="line" />
            </div>
            <div className="mt-6 grid gap-6 lg:grid-cols-3">
              <SkeletonChartCard kind="mix" />
              <SkeletonChartCard kind="bars" />
              <SkeletonChartCard kind="bars" />
            </div>
          </>
        }
      >
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {platformKpis.map((kpi) => (
              <StatTile key={kpi.label} label={kpi.label} value={kpi.value} />
            ))}
          </div>

          <h2 className="mt-10 font-heading text-lg font-semibold text-ink">Analytics da plataforma</h2>
          <p className="text-sm text-ink-secondary">Últimos 6 meses · mensalidade SaaS + 2% sobre transações.</p>

          <div className="mt-4 grid gap-6 lg:grid-cols-2">
            <ChartCard
              title="MRR (receita recorrente das assinaturas)"
              tableHeaders={['Mês', 'MRR']}
              tableRows={mrrTrend.map((point) => [point.label, formatCurrency(point.value)])}
            >
              <RevenueTrendChart points={mrrTrend} />
            </ChartCard>

            <ChartCard
              title="Volume transacionado (GMV)"
              subtitle="Total processado pela plataforma"
              tableHeaders={['Mês', 'Volume']}
              tableRows={gmvTrend.map((point) => [point.label, formatCurrency(point.value)])}
            >
              <RevenueTrendChart points={gmvTrend} />
            </ChartCard>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-3">
            <ChartCard
              title="Split da receita (mês)"
              subtitle="Para onde vai cada real transacionado"
              tableHeaders={['Destino', 'Valor']}
              tableRows={revenueSplit.map((item) => [item.label, formatCurrency(item.value)])}
            >
              <ProductMixBar items={revenueSplit} formatValue={formatCurrency} />
            </ChartCard>

            <ChartCard
              title="Arenas por plano"
              tableHeaders={['Plano', 'Arenas']}
              tableRows={arenasByPlan.map((item) => [item.label, item.value])}
            >
              <MagnitudeBarList items={arenasByPlan} color={CHART_INK.brand} formatValue={formatArenaCount} />
            </ChartCard>

            <ChartCard
              title="Arenas por status de assinatura"
              tableHeaders={['Status', 'Arenas']}
              tableRows={arenasByStatus.map((item) => [item.label, item.value])}
            >
              <MagnitudeBarList items={arenasByStatus} color={CHART_INK.brand} formatValue={formatArenaCount} />
            </ChartCard>
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_320px]">
            <div className="flex flex-col gap-6">
              <ArenasTable arenas={arenas} />
              <TransactionsTable transactions={transactions} />
            </div>

            <div className="flex flex-col gap-6">
              <DisputesQueue disputes={disputes} />
              <ModerationQueue items={moderationQueue} />
            </div>
          </div>

          <h2 className="mt-10 font-heading text-lg font-semibold text-ink">Webhooks & Integrações</h2>
          <p className="text-sm text-ink-secondary">
            Erros de integração e reprocessamento de eventos de pagamento, por provedor.
          </p>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {webhookKpis.map((kpi) => (
              <StatTile key={kpi.label} label={kpi.label} value={kpi.value} />
            ))}
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1.2fr]">
            <ChartCard
              title="Taxa de sucesso de webhooks"
              subtitle="Últimos 7 dias"
              tableHeaders={['Dia', 'Taxa de sucesso']}
              tableRows={webhookSuccessTrend.map((point) => [point.label, formatPercent(point.value)])}
            >
              <RevenueTrendChart
                points={webhookSuccessTrend}
                formatValue={formatPercent}
                ariaLabel="Taxa de sucesso de webhooks"
              />
            </ChartCard>

            <WebhookEventsTable events={webhookEvents} />
          </div>
      </AsyncSection>
    </section>
  );
}
